import TileSet from './tileset'
import ServerApi from './server-api'

/* eslint-disable no-param-reassign */

/**
 * Класс для работы с ресурсами. Необходим для получения ресурсов за O(1)
 */
class Resources {
  constructor(data) {
    this.resources = data
    this.parseResources()
    this.resourcesToDelete = new Set()
    this.onLoadFunc = null
  }

  parseResources() {
    this.audioFilesById = {} // Записи об аудио-ресурсах
    this.audioBlobById = {} // Загруженные аудио-Blob'ы
    this.audioById = {} // Загруженные HTML-аудиотеги
    this.texturesById = {} // Записи об ресурсах-текстурах
    this.imagesById = {} // Загруженные HTML-изображения
    this.gifsById = {} // Загруженные gif-ы в формате ArrayBuffer
    this.tilesets = {} // tileset'ы
    this.resources.forEach((resource) => {
      switch (resource.type) {
        case 'resource-texture': {
          const src = `/api/files/${resource.file.physicalName}`
          this.texturesById[resource.id] = resource
          if (resource.file.physicalName.endsWith('.gif')) {
            ServerApi.getGif(src).then((response) => {
              this.gifsById[resource.id] = response.data
              this.checkOnload()
            })
          } else {
            const imgLoad = new Image()
            imgLoad.onload = () => {
              this.imagesById[resource.id] = imgLoad
              this.checkOnload()
            }
            imgLoad.src = src
          }
          break
        }
        case 'resource-audio':
          this.audioFilesById[resource.id] = resource
          ServerApi.getAudioBlob(
            `/api/files/${resource.file.physicalName}`,
          ).then((response) => {
            const blob = response.data
            this.audioBlobById[resource.id] = blob
            this.audioById[resource.id] = new Audio(URL.createObjectURL(blob))
            this.checkOnload()
          })
          break
        case 'resource-tileset':
          this.tilesets[resource.id] = new TileSet(resource)
          this.tilesets[resource.id].onload = () => {
            this.checkOnload()
          }
          break
        default:
          throw new Error(`Неизвестный тип ресурса: "${resource.type}"`)
      }
    })
  }

  checkOnload() {
    if (this.isLoaded && this.onLoadFunc !== null) {
      this.onLoadFunc()
    }
  }

  set onload(newOnLoad) {
    this.onLoadFunc = newOnLoad
    this.checkOnload()
  }

  get isLoaded() {
    return (
      Object.keys(this.imagesById).length
        + Object.keys(this.gifsById).length
        === Object.keys(this.texturesById).length
      && Object.values(this.tilesets).every((tileset) => tileset.isLoaded)
      && Object.keys(this.audioBlobById).length
        === Object.keys(this.audioFilesById).length
    )
  }

  /**
   * Добавить текстуру или аудио
   *
   * @param {Array.<Object>} uploadedFiles Загруженные на сервер файлы
   */
  addResources(uploadedFiles) {
    uploadedFiles.forEach((file) => {
      const type = file.type === 'audio' ? 'resource-audio' : 'resource-texture'
      const resource = {
        id: file.id,
        new: true,
        type,
        description: file.description,
        fileId: file.id,
        file
      }
      this.resources.push(resource)
      if (type === 'resource-audio') {
        this.audioById[resource.id] = resource
      } else {
        this.texturesById[resource.id] = resource
      }
    })
  }

  /**
   * Добавить тайлсет
   *
   * @param {Object} imageFile Загруженный на сервер файл с изображением
   * @param {Object} tilesetJson описание тайлсета
   */
  addTileset(imageFile, tilesetJson) {
    const resource = {
      id: imageFile.id,
      new: true,
      description: tilesetJson,
      name: imageFile.name,
      physicalName: imageFile.physicalName,
      type: 'resource-tileset',
      fileId: imageFile.id,
      file: imageFile
    }
    this.resources.push(resource)
    this.tilesets[resource.id] = new TileSet(resource)
  }

  /**
   * Удаление ресурса
   *
   * @param {number} id id ресурса
   */
  deleteResource(id) {
    const res = this.resources.find((resource) => resource.id === id)
    this.resources = this.resources.filter((resource) => resource.id !== id)
    if (this.audioById[id]) {
      delete this.audioById[id]
    }
    if (this.texturesById[id]) {
      delete this.texturesById[id]
    }
    if (!res.new) {
      this.resourcesToDelete.add(id)
    }
  }

  /**
   * Получить ресурсы для отправки. Отличаются от обычных обновленным содержимым тайлсетов
   *
   * @returns {Array}
   */
  get resourcesToSend() {
    const { tilesets } = this
    return this.resources.map((resource) => {
      const { id } = resource
      if (resource.type !== 'resource-tileset') {
        if (resource.new) {
          delete resource.new
          delete resource.id
        }
        return resource
      }
      return tilesets[id].resource
    })
  }
}

export default Resources
